import type { Project, ProjectInfo, WallInput } from "@tasduvar/engine";
import { fmt, makeDefaultWall } from "@tasduvar/engine";
import { parseTR } from "../../lib/number";
import { useProjectStore, type SeismicInit } from "../../state/projectStore";
import { useAutoSizeStore } from "../../state/useAutoSize";
import { useWizardStore } from "../../state/wizardStore";
import { t } from "../../i18n";
import { BUILDING_WARN, pickFoundationSoil, type Found, type LocalReport } from "../extract/localParser";
import { phiCheck, soilType } from "./soilTypes";

/**
 * Akıllı başlangıç penceresi modeli (saf): alan metinleri, kaynak rozetleri (rapordan s. N / varsayılan), belirsiz aday
 * ipuçları, doğrulama ve duvar reçetesi. Rapordan okunamayan zemin değerleri boş kalır; kullanıcı doldurur (Cem 2026-09-29).
 */
export type SmartKey = "H" | "L" | "bfGamma" | "bfPhi" | "bfC" | "beta" | "q" | "fGamma" | "fPhi" | "fC" | "sigma" | "df" | "gwDepth" | "SS" | "S1";
export type Site = "ZA" | "ZB" | "ZC" | "ZD" | "ZE";
export interface SmartSource {
  /** report: rapordan s. N · default: program varsayılanı · typical: seçilen zemin türünün tipik değeri · location: konumdan (AFAD ızgarası) */
  kind: "report" | "default" | "typical" | "location"; page?: number; snippet?: string; note?: string;
  /** kontrol uyarısı (ör. bina temeli için verilmiş) */ warn?: string;
  /** typical: zemin türü kodu (ör. "SP") */ soil?: string;
}
/** replaced: rapordaki değer zemin türü seçimiyle değiştirildi (geri almak için "bunu kullan") */
export interface SmartHint { text: string; value?: number; kind?: "replaced" }
export interface SmartForm {
  text: Record<SmartKey, string>;
  src: Partial<Record<SmartKey | "gw" | "site", SmartSource>>;
  hints: Partial<Record<SmartKey | "gw" | "site", SmartHint>>;
  gw: "none" | "depth"; eqOn: boolean; site: Site; undrained: boolean;
  /** YAS girişi: depth = dolgu üstünden derinlik · height = duvar tabanı üstünden yükseklik */
  gwRef: "depth" | "height";
  /** Seçilen zemin türü (USCS kodu; "" = seçilmedi): bf = dolgu, f = temel zemini */
  bfType: string; fType: string;
  /** Rapordaki drenajsız kayma dayanımı c_u (drenajsız hesaba geçince c alanına yazılır) */
  cu?: Found;
  /** Drenajsıza geçmeden önceki φ, c (drenajlıya dönünce geri gelir) */
  stash?: { fPhi: string; fC: string; srcPhi?: SmartSource; srcC?: SmartSource };
  /** Temel zemini tabakası (rapor tablosu); -1 = seçilmedi */
  layer: number;
}
export interface SmartStart { recipe: (w: WallInput) => void; height: number }

/** Zorunlu alanlar (boşsa "Analize başla" kapalı) */
export const REQUIRED: SmartKey[] = ["H", "bfPhi", "fPhi"];

/**
 * Boş bırakılabilen alanların ortalama değeri — alanda silik yer tutucu olarak görünür, boşsa hesapta bu kullanılır
 * (Cem 2026-09-30: "birim hacim ağırlığı boş geliyor; silik şekilde ortalama değer yazsın"). Yeni duvarın varsayılanlarıyla aynı.
 */
const DW = makeDefaultWall();
export const SMART_DEFAULTS = { bfGamma: DW.backfill.layers[0]!.gamma, fGamma: DW.foundationSoil.gamma } as const satisfies Partial<Record<SmartKey, number>>;
const RANGE: Record<SmartKey, [number, number]> = {
  H: [0.5, 15], L: [1, 10000], bfGamma: [10, 25], bfPhi: [0, 50], bfC: [0, 200], beta: [0, 45], q: [0, 200],
  fGamma: [10, 25], fPhi: [0, 50], fC: [0, 500], sigma: [10, 2000], df: [0, 5], gwDepth: [0, 60], SS: [0.001, 3], S1: [0.001, 1.5],
};
const DEFAULT: SmartSource = { kind: "default" };

export function emptySmartForm(seed?: { eqOn?: boolean; ss?: string; s1?: string; site?: Site }): SmartForm {
  const text = Object.fromEntries((Object.keys(RANGE) as SmartKey[]).map((k) => [k, ""])) as Record<SmartKey, string>;
  text.beta = "0"; text.q = "10"; text.df = "1,00"; text.SS = seed?.ss ?? ""; text.S1 = seed?.s1 ?? "";
  return { text, src: { beta: DEFAULT, q: DEFAULT, df: DEFAULT }, hints: {}, gw: "none", gwRef: "depth", bfType: "", fType: "", eqOn: seed?.eqOn ?? true, site: seed?.site ?? "ZC", undrained: false, layer: -1 };
}

const num = (v: number, d: number): string => fmt(v, d);
const srcOf = (f: Found<unknown>): SmartSource => ({ kind: "report", page: f.page, snippet: f.snippet, note: f.note });
const hintOf = (f: Found, d: number): SmartHint => ({ text: `${num(f.value, d)} (s. ${f.page})${f.note ? ` — ${f.note}` : ""}`, value: f.value });

/** Rapor okumasını forma işler. Güvenilir değerler yazılır (rozetli); düşük güvenliler boş bırakılır, ipucu olarak gösterilir. */
export function applyReport(form: SmartForm, r: LocalReport, layerIdx?: number): SmartForm {
  const f: SmartForm = { ...form, text: { ...form.text }, src: { ...form.src }, hints: { ...form.hints } };
  const put = (k: SmartKey, x: Found | undefined, d: number): void => {
    delete f.hints[k];
    if (!x) return;
    if (x.confidence === "low") { f.hints[k] = hintOf(x, d); f.text[k] = ""; delete f.src[k]; return; }
    f.text[k] = num(x.value, d); f.src[k] = srcOf(x);
  };
  // Temel zemini (pasif taraf)
  const pick = pickFoundationSoil(r, layerIdx);
  f.layer = pick.layer; f.undrained = !!pick.undrained; f.stash = undefined; f.fType = "";   // rapor değerleri türün yerine geçer
  f.cu = pick.cu && pick.cu.confidence !== "low" ? pick.cu : undefined;
  for (const k of ["fGamma", "fPhi", "fC"] as const) { f.text[k] = ""; delete f.src[k]; }
  put("fGamma", pick.gamma, 1); put("fPhi", pick.phi, 1); put("fC", pick.c, 1);
  if (!pick.gamma && r.values.gamma?.confidence === "low") f.hints.fGamma = hintOf(r.values.gamma, 1);
  if (!pick.phi && r.values.phi?.confidence === "low") f.hints.fPhi = hintOf(r.values.phi, 1);
  if (r.layers.length > 1 && pick.layer < 0) f.hints.fGamma ??= { text: "Birden çok tabaka var, temel derinliği bulunamadı — tabakayı seçin" };
  if (layerIdx !== undefined) return f;   // yalnız tabaka değişti
  // Aktif taraf (geri dolgu önerisi)
  if (r.backfill.gamma || r.backfill.phi || r.backfill.c) f.bfType = "";
  put("bfGamma", r.backfill.gamma, 1); put("bfPhi", r.backfill.phi, 1); put("bfC", r.backfill.c, 1);
  put("sigma", r.values.qa, 0);
  if (r.values.df) put("df", r.values.df, 2);
  // Bina etüdü: Df ve taşıma gücü bina temeli içindir → dolu gelir ama uyarılır (Cem 2026-09-29: "uyarı gelsin")
  if (r.building) for (const k of ["sigma", "df"] as const) if (f.src[k]?.kind === "report") f.src[k] = { ...f.src[k]!, warn: BUILDING_WARN };
  if (r.groundwater) {
    if (r.groundwater.confidence === "low") f.hints.gw = { text: `${r.groundwater.value === null ? "rastlanmadı" : `${num(r.groundwater.value, 2)} m`} (s. ${r.groundwater.page})` };
    else if (r.groundwater.value === null) { f.gw = "none"; f.src.gw = srcOf(r.groundwater); f.text.gwDepth = ""; }
    else { f.gw = "depth"; f.gwRef = "depth"; f.src.gw = srcOf(r.groundwater); f.text.gwDepth = num(r.groundwater.value, 2); f.src.gwDepth = srcOf(r.groundwater); }
  } else f.hints.gw = { text: "Raporda yeraltı suyu bilgisi bulunamadı — kontrol edin" };
  // Deprem: formda elle girilmiş SS/S1 korunur
  if (!form.text.SS.trim() && !form.text.S1.trim()) { put("SS", r.values.SS, 3); put("S1", r.values.S1, 3); }
  if (!f.text.SS && !f.text.S1 && r.values.SDS && r.values.SDS.confidence !== "low") f.hints.SS = { text: `Raporda yalnız SDS = ${num(r.values.SDS.value, 3)} (s. ${r.values.SDS.page}); SS ve S1'i AFAD haritasından girin` };
  const sc = r.siteClass;
  if (sc && sc.confidence !== "low" && /^Z[A-E]$/.test(sc.value)) { f.site = sc.value as Site; f.src.site = srcOf(sc); }
  else if (sc) f.hints.site = { text: `${sc.value} (s. ${sc.page})${sc.value === "ZF" ? " — ZF: sahaya özel analiz gerekir" : sc.note ? ` — ${sc.note}` : ""}` };
  return f;
}

/** Kullanıcı bir alanı değiştirdi: rapor rozeti kalkar (değer artık kullanıcının) */
export function setField(f: SmartForm, k: SmartKey, v: string): SmartForm {
  const src = { ...f.src }; delete src[k];
  return { ...f, text: { ...f.text, [k]: v }, src };
}

/** Dolgu kohezyonu itkiyi azaltır (c = 40 → statik itki ≈ 0, güvensiz); kopyalamada ve tür seçiminde 0 yazılır */
export const BF_C0 = "Dolguda kohezyon güvenli tarafta 0 alınır";

const SOIL_KEYS = { bf: ["bfGamma", "bfPhi", "bfC"], f: ["fGamma", "fPhi", "fC"] } as const;

/**
 * Zemin türü seçildi (Cem 2026-10-05: "zemin tipi seçiniz; seçilince ortalama φ, γ, c dolsun; sonradan değiştirilebilsin"):
 * γ, φ, c türün tipik değerleriyle dolar (rozet "tip."; dolguda sıkıştırılmış dolgu değerleri, c = 0). Kullanıcı bilinçli seçtiği için rapor değerinin üstüne yazar, ama
 * rapordan gelen değer ipucu olarak kalır ("Rapor: 4 (s. 3) — bunu kullan"). Boş kod: yalnız seçim kalkar, değerler durur.
 */
export function applySoilType(form: SmartForm, side: "bf" | "f", code: string): SmartForm {
  const f: SmartForm = { ...form, text: { ...form.text }, src: { ...form.src }, hints: { ...form.hints }, [side === "bf" ? "bfType" : "fType"]: code };
  const s = soilType(code);
  if (!s) return f;
  const [kG, kP, kC] = SOIL_KEYS[side];
  // Dolgu: sıkıştırılmış dolgu değerleri (c' = 0) · temel: doğal zemin değerleri
  const vals = side === "bf" ? [s.gammaFill, s.phiFill, s.cFill] : [s.gamma, s.phi, s.c];
  for (const [k, v, d] of [[kG, vals[0]!, 1], [kP, vals[1]!, 0], [kC, vals[2]!, 0]] as const) {
    const old = form.src[k];
    if (old?.kind === "report" && form.text[k].trim()) f.hints[k] = { text: `${form.text[k]} (s. ${old.page})`, value: parseTR(form.text[k]) ?? undefined, kind: "replaced" };
    f.text[k] = fmt(v, d); f.src[k] = { kind: "typical", soil: s.code };
  }
  if (side === "bf") { f.text[kC] = "0"; f.src[kC] = { kind: "typical", soil: s.code, note: BF_C0 }; }   // dolguda c güvenli tarafta 0
  if (side === "f") { f.undrained = false; f.stash = undefined; }
  return f;
}

/**
 * "Temel zemininden kopyala": γ, φ ve zemin türü dolguya geçer; c kopyalanmaz (0). Drenajsız temelin φ = 0'ı dolguya geçmez.
 */
export function copyFoundation(form: SmartForm): SmartForm {
  const typ = (k: "fGamma" | "fPhi"): SmartSource | undefined => (form.src[k]?.kind === "typical" ? form.src[k] : undefined);
  return {
    ...form, bfType: form.fType,
    text: { ...form.text, bfGamma: form.text.fGamma, bfPhi: form.undrained ? "" : form.text.fPhi, bfC: "0" },
    src: { ...form.src, bfGamma: typ("fGamma"), bfPhi: form.undrained ? undefined : typ("fPhi"), bfC: { kind: "default", note: BF_C0 } },
  };
}

/**
 * Temel zemini koşulu (teşhis 2026-10-05: raporda φ = 4° ile drenajlı N·tanδ hesabı çözümsüz; drenajsız Ac·cu ile çözüm çıkar):
 * drenajsızda φ = 0, c = cu (rapordan okunduysa yazılır, yoksa mevcut c kalır); drenajlıya dönünce önceki φ, c geri gelir.
 */
export function setUndrained(form: SmartForm, on: boolean): SmartForm {
  if (form.undrained === on) return form;
  const text = { ...form.text }, src = { ...form.src };
  if (on) {
    const stash = { fPhi: form.text.fPhi, fC: form.text.fC, srcPhi: form.src.fPhi, srcC: form.src.fC };
    text.fPhi = "0"; src.fPhi = { kind: "default", note: "drenajsız: φ = 0" };
    // Rapor c_u okunduysa o; yoksa mevcut c (UU deneyinde φ ≈ 0–5° ile verilen c zaten c_u'dur) korunur
    if (form.cu) { text.fC = num(form.cu.value, 1); src.fC = srcOf(form.cu); }
    return { ...form, undrained: true, text, src, stash };
  }
  const st = form.stash;
  text.fPhi = st?.fPhi ?? ""; src.fPhi = st?.srcPhi; text.fC = st?.fC ?? ""; src.fC = st?.srcC;
  return { ...form, undrained: false, text, src, stash: undefined };
}

/** YAS'ın duvar tabanı üstündeki yüksekliği (m); YAS yoksa ya da derinlik girişinde H bilinmiyorsa undefined. ≤ 0 → taban altında */
export function gwAboveBase(f: SmartForm, v: Partial<Record<SmartKey, number>>, H = v.H): number | undefined {
  if (f.gw !== "depth" || v.gwDepth === undefined) return undefined;
  if (f.gwRef === "height") return v.gwDepth;
  return H === undefined ? undefined : Math.round((H - v.gwDepth) * 100) / 100;
}

/** YAS giriş biçimi değişti: H biliniyorsa girilen değer yeni biçime çevrilir (derinlik ↔ taban üstü yükseklik) */
export function setGwRef(f: SmartForm, ref: SmartForm["gwRef"]): SmartForm {
  if (f.gwRef === ref) return f;
  const H = parseTR(f.text.H.trim()), x = parseTR(f.text.gwDepth.trim());
  const conv = H !== null && x !== null && H - x >= 0 ? fmt(H - x, 2) : f.text.gwDepth;
  const src = { ...f.src }; delete src.gwDepth;
  return { ...f, gwRef: ref, text: { ...f.text, gwDepth: conv }, src };
}

export interface SmartParsed {
  values: Partial<Record<SmartKey, number>>; errors: Partial<Record<SmartKey, string>>; missing: SmartKey[];
  /** Hesabı engellemeyen uyarılar (ör. φ türün tipik aralığının çok altında) */
  warnings: Partial<Record<SmartKey, string>>;
  /** Temel φ çok küçük → "Drenajsız hesapla" önerisi */
  suggestUndrained: boolean;
}
export function parseSmart(f: SmartForm): SmartParsed {
  const values: Partial<Record<SmartKey, number>> = {}, errors: Partial<Record<SmartKey, string>> = {};
  for (const k of Object.keys(RANGE) as SmartKey[]) {
    const s = f.text[k].trim();
    if (!s) continue;
    if ((k === "SS" || k === "S1") && !f.eqOn) continue;
    if (k === "gwDepth" && f.gw !== "depth") continue;
    const n = parseTR(s);
    const [lo, hi] = RANGE[k];
    if (n === null) errors[k] = "Geçerli bir sayı girin";
    else if (n < lo || n > hi) errors[k] = `${fmt(lo, lo < 1 && lo > 0 ? 3 : 0)} – ${fmt(hi, 0)} arası olmalı`;
    else values[k] = n;
  }
  if (values.beta !== undefined && values.bfPhi !== undefined && values.beta >= values.bfPhi && values.beta > 0) errors.beta = "β, dolgu φ'sinden küçük olmalı";
  if (f.eqOn && (values.SS === undefined) !== (values.S1 === undefined) && !errors.SS && !errors.S1) errors[values.SS === undefined ? "SS" : "S1"] = "SS ve S1 birlikte girilmeli";
  if (f.gw === "depth" && values.gwDepth === undefined && !errors.gwDepth) errors.gwDepth = f.gwRef === "height" ? "Yükseklik girin" : "Derinlik girin";
  if (f.gw === "depth" && f.gwRef === "height" && values.gwDepth !== undefined && values.H !== undefined && values.gwDepth > values.H) errors.gwDepth = "Duvar yüksekliğinden (H) büyük olamaz";
  const missing = [...REQUIRED, ...(f.undrained ? ["fC" as const] : [])].filter((k) => values[k] === undefined && !errors[k]);
  const warnings: SmartParsed["warnings"] = {};
  const fromReport = (k: SmartKey): boolean => f.src[k]?.kind === "report" || (f.hints[k]?.value !== undefined && f.hints[k]!.value === values[k]);
  const bfW = f.bfType ? phiCheck(f.bfType, values.bfPhi, fromReport("bfPhi"))
    : values.bfPhi !== undefined && values.bfPhi < 15 ? t("Dolgu φ çok düşük — itki çok büyür, çözüm çıkmayabilir. Geri dolgu genelde granüler malzemedir (ör. GW/SW, φ ≈ 33–36°); zemin türü seçin.") : null;
  if (bfW) warnings.bfPhi = bfW;
  if (values.bfC !== undefined && values.bfC > 0) warnings.bfC = t("Dolgu kohezyonu itkiyi azaltır, güvensiz olabilir; genelde c = 0 alınır.");
  // Temel φ < 10° (granüler tür seçili değilse): büyük olasılıkla drenajsız deney → drenajsız hesap önerisi (phiCheck'in yerine)
  const suggestUndrained = !f.undrained && values.fPhi !== undefined && values.fPhi < 10 && !soilType(f.fType)?.granular;
  const fW = f.fType && !f.undrained && !suggestUndrained ? phiCheck(f.fType, values.fPhi, fromReport("fPhi")) : null;
  if (fW) warnings.fPhi = fW;
  return { values, errors, missing, warnings, suggestUndrained };
}

/** "Analize başla" öncesi düşük φ onayı (Cem 2026-10-05: "4° girince 'çok düşük, emin misiniz?' yönlendirmesi") */
export interface PhiAlert {
  key: "bfPhi" | "fPhi"; phi: number;
  /** "Tipik değeri kullan" önerisi: seçilen türün φ'si; tür yoksa dolguda 33°, temelde yok (tür seçmeye yönlendirilir) */
  typical?: number; soil?: string;
  /** Temel tarafı ve c > 0: drenajsız (φ = 0, cu) hesap önerilebilir */
  canUndrained: boolean;
}
const BF_PHI_LOW = 20, F_PHI_LOW = 15, BF_PHI_TYPICAL = 33;
export function lowPhiAlerts(f: SmartForm, p: SmartParsed): PhiAlert[] {
  const out: PhiAlert[] = [];
  const bf = p.values.bfPhi, fp = p.values.fPhi;
  if (bf !== undefined && (bf < BF_PHI_LOW || phiCheck(f.bfType, bf))) {
    const s = soilType(f.bfType);
    out.push({ key: "bfPhi", phi: bf, typical: s?.phiFill ?? BF_PHI_TYPICAL, soil: s?.code, canUndrained: false });
  }
  if (!f.undrained && fp !== undefined && (fp < F_PHI_LOW || phiCheck(f.fType, fp))) {
    const s = soilType(f.fType);
    out.push({ key: "fPhi", phi: fp, typical: s?.phi, soil: s?.code, canUndrained: (p.values.fC ?? 0) > 0 });
  }
  return out.filter((a) => a.typical === undefined || a.phi < a.typical);
}

/** Onay kartındaki "Tipik değeri kullan": φ alanına türün (ya da dolgu için 33°) değeri yazılır */
export function applyTypicalPhi(f: SmartForm, a: PhiAlert): SmartForm {
  if (a.typical === undefined) return f;
  const g = setField(f, a.key, fmt(a.typical, 0));
  return a.soil ? { ...g, src: { ...g.src, [a.key]: { kind: "typical", soil: a.soil } } } : g;
}

/**
 * Başlamadan önce kontrol kartı (Cem 2026-10-06: "programımız yönlendirsin doğru yola"): düşük φ'nin yanında duvar arkasında
 * tabanın üstünde su (drenaj varsayımı yok → su itkisi tabanı büyütür) ve dolgu c > 0 (itkiyi azaltır, güvensiz) da kartı açar.
 * Tek seferliktir: "Evet, eminim — devam et" ile geçilir.
 */
export interface StartChecks {
  phi: PhiAlert[];
  /** YAS duvar tabanının bu kadar (m) üstünde ve su hesaba katılıyor */ water?: number;
  /** Dolgu kohezyonu c > 0 (kN/m²) */ bfC?: number;
}
export function startChecks(f: SmartForm, p: SmartParsed): StartChecks {
  const hw = gwAboveBase(f, p.values);
  const c = p.values.bfC;
  return { phi: lowPhiAlerts(f, p), water: hw !== undefined && hw > 0 ? hw : undefined, bfC: c !== undefined && c > 0 ? c : undefined };
}
export const hasStartChecks = (c: StartChecks): boolean => c.phi.length > 0 || c.water !== undefined || c.bfC !== undefined;

/** "Suyu hesaba katma" (barbakan/dren yapılacak): YAS "Yok" */
export function ignoreWater(f: SmartForm): SmartForm {
  return { ...f, gw: "none", src: { ...f.src, gw: undefined } };
}
/** "Dolguda c = 0 al" */
export function zeroBackfillC(f: SmartForm): SmartForm {
  const g = setField(f, "bfC", "0");
  return { ...g, src: { ...g.src, bfC: { kind: "default", note: BF_C0 } } };
}

/** φ eksikse kullanıcıyı zemin türü seçimine yönlendirmek için taraf (önce dolgu); eksik φ yoksa null */
export function missingPhiSide(p: SmartParsed): "bf" | "f" | null {
  return p.missing.includes("bfPhi") ? "bf" : p.missing.includes("fPhi") ? "f" : null;
}

/** "Konumdan al": SS/S1 AFAD TDTH ızgarasından (rozet "konum"); deprem hesabı açılır */
export function applyLocation(f: SmartForm, p: { SS: number; S1: number }): SmartForm {
  const hints = { ...f.hints }; delete hints.SS; delete hints.S1;
  return { ...f, eqOn: true, text: { ...f.text, SS: fmt(p.SS, 3), S1: fmt(p.S1, 3) }, src: { ...f.src, SS: { kind: "location" }, S1: { kind: "location" } }, hints };
}

export function smartSeismic(f: SmartForm, v: SmartParsed["values"]): SeismicInit | undefined {
  if (!f.eqOn) return { enabled: false };
  if (v.SS === undefined || v.S1 === undefined) return undefined;
  return { enabled: true, SS: v.SS, S1: v.S1, siteClass: f.site };
}

/** Deprem SS/S1 boş bırakılsa da zemin sınıfı duvara yazılır */
export function smartRecipe(f: SmartForm, v: SmartParsed["values"]): SmartStart {
  const H = v.H ?? 3;
  return {
    height: H,
    recipe: (w) => {
      const l0 = w.backfill.layers[0]!;
      w.backfill.layers[0] = { ...l0, gamma: v.bfGamma ?? SMART_DEFAULTS.bfGamma, phi: v.bfPhi ?? l0.phi, c: v.bfC ?? 0, thickness: Math.max(l0.thickness, Math.ceil(H + 1)) };
      w.backfill.slopeAngle = v.beta ?? 0;
      w.loads.surcharge = v.q ?? 0;
      const fs = w.foundationSoil;
      // Drenajsız: φ = 0, c = cu (kayma Ac·cu) · drenajlı: φ', c' (N·tanδ)
      fs.gamma = v.fGamma ?? SMART_DEFAULTS.fGamma; fs.phi = f.undrained ? 0 : v.fPhi ?? fs.phi; fs.c = v.fC ?? 0;
      fs.drained = !f.undrained;
      fs.bearing = v.sigma !== undefined ? { ...fs.bearing, mode: "given", sigmaAllow: v.sigma, qk: undefined } : { ...fs.bearing, mode: "computed", sigmaAllow: undefined, qk: undefined };
      if (v.df !== undefined) w.geometry.embedmentDepth = v.df;
      const level = gwAboveBase(f, v, H);
      w.water.backLevel = level !== undefined && level > 0 ? level : null;
      if (v.L !== undefined) w.length = v.L;
      if (f.eqOn) w.seismic.siteClass = f.site;
    },
  };
}

/** Künye: formdaki boş il/ilçe/ada/parsel rapordan tamamlanır */
export function mergeInfo(info: Partial<ProjectInfo>, r: LocalReport | null): Partial<ProjectInfo> {
  if (!r) return info;
  const s = r.site;
  return { ...info, city: info.city || s.city?.value, district: info.district || s.district?.value, block: info.block || s.block?.value, parcel: info.parcel || s.parcel?.value };
}

const SITE_KEYS = ["city", "district", "block", "parcel"] as const;
/**
 * Sihirbaz proje içinden (mevcut künye): künyeye eklenecek alanlar — yalnız BOŞ il/ilçe/ada/parsel rapordan (mergeInfo kuralı,
 * dolu alan ezilmez); konum künyede yoksa ya da kullanıcı pencerede "Konumdan al"ı kullandıysa (`picked`) yazılır.
 * Yeni bilgi yoksa boş nesne → künyeye dokunulmaz.
 */
export function wizardInfoPatch(info: Partial<ProjectInfo>, r: LocalReport | null, loc?: ProjectInfo["location"], picked = false): Partial<ProjectInfo> {
  const merged = mergeInfo(info, r), out: Partial<ProjectInfo> = {};
  for (const k of SITE_KEYS) if (!info[k]?.trim() && merged[k]?.trim()) out[k] = merged[k];
  const cur = info.location;
  if (loc && (picked || !cur) && (loc.lat !== cur?.lat || loc.lon !== cur?.lon)) out.location = loc;
  return out;
}
const SITE_LABEL: Record<(typeof SITE_KEYS)[number], string> = { city: "İl", district: "İlçe", block: "Ada", parcel: "Parsel" };
const deg = (x: number): string => x.toFixed(5).replace(".", ",");
/** Künye yamasının kısa özeti: "İl Bolu · Ada 123 · Parsel 4 · Konum 40,12345° · 31,60000°" (boşsa "") */
export function infoPatchText(p: Partial<ProjectInfo>): string {
  const parts = SITE_KEYS.filter((k) => p[k]).map((k) => `${t(SITE_LABEL[k])} ${p[k]}`);
  if (p.location) parts.push(`${t("Konum")} ${deg(p.location.lat)}° · ${deg(p.location.lon)}°`);
  return parts.join(" · ");
}

/**
 * Yeni projeye akıllı başlangıcı uygular (geri alma geçmişine yazmadan): duvar reçetesi + Akıllı tasarım paftasının H girdisi
 * + analiz isteği (pafta açılınca tam erişim varsa tarama başlar).
 */
export function applySmartStart(p: Project, s: SmartStart): Project {
  const w = structuredClone(p.walls[0]!);
  s.recipe(w);
  const next: Project = { ...p, walls: [w, ...p.walls.slice(1)] };
  // Kayıt noktası da (baseline) pencere değerlerini taşır: "Kaydetme" boş duvara değil bu ilk hâle döner
  useProjectStore.setState((st) => (st.baseline === p ? { project: next, baseline: next } : { project: next }));
  useWizardStore.getState().markSoil(w.id);
  const a = useAutoSizeStore.getState();
  a.reset();
  a.setInputs(w.id, { height: s.height, length: w.length ?? 10 });
  a.requestRun(w.id, { fresh: true });
  return next;
}

/** Taranabilir tip (design/resize.ts resizeKind ile aynı kural; ağır pafta modülünü buraya çekmemek için ayrı): parametrik taş ya da payandasız konsol */
export const scannable = (w: WallInput): boolean => w.geometry.mode === "parametric" || (w.geometry.mode === "rc-cantilever" && !w.geometry.counterforts);

/** Duvardaki sayı → alan metni (en çok 3 ondalık, binlik ayırıcısız; parseTR geri okur) */
const tx = (v: number): string => String(Math.round(v * 1000) / 1000).replace(".", ",");

/**
 * Sihirbaz proje içinden açıldı (Cem 2026-10-06): form seçili duvarın değerleriyle ön doldurulur (rozetsiz — değerler zaten
 * duvarın). H = mevcut yükseklik (ya da Akıllı tasarım girdisi), L, dolgu γ/φ/c/β, q, temel γ/φ/c/drenaj, σem (verilmişse),
 * Df, YAS (taban üstü yükseklik olarak), deprem SS/S1/sınıf/açık-kapalı. Zemin türü bilinmez → seçilmemiş.
 */
export function formFromWall(w: WallInput, height: number, length = w.length): SmartForm {
  const s = w.seismic, b = w.backfill.layers[0]!, fs = w.foundationSoil;
  const site: Site = s.siteClass && /^Z[A-E]$/.test(s.siteClass) ? (s.siteClass as Site) : "ZC";
  const f = emptySmartForm({ eqOn: s.enabled, ss: s.SS !== undefined ? tx(s.SS) : "", s1: s.S1 !== undefined ? tx(s.S1) : "", site });
  f.src = {};
  Object.assign(f.text, {
    H: fmt(height, 2), L: length !== undefined ? tx(length) : "",
    bfGamma: tx(b.gamma), bfPhi: tx(b.phi), bfC: tx(b.c), beta: tx(w.backfill.slopeAngle), q: tx(w.loads.surcharge),
    fGamma: tx(fs.gamma), fPhi: tx(fs.phi), fC: tx(fs.c),
    sigma: fs.bearing.mode === "given" && fs.bearing.sigmaAllow !== undefined ? tx(fs.bearing.sigmaAllow) : "",
    df: fmt(w.geometry.embedmentDepth ?? 1, 2),
  });
  if (fs.drained === false) { f.undrained = true; f.text.fPhi = "0"; f.src.fPhi = { kind: "default", note: "drenajsız: φ = 0" }; }
  const lv = w.water.backLevel;
  if (lv !== null && lv !== undefined && lv > 0) { f.gw = "depth"; f.gwRef = "height"; f.text.gwDepth = fmt(Math.min(lv, height), 2); }
  return f;
}

/**
 * Sihirbaz değerlerini mevcut duvara yazar (saf; `updateWall` reçetesi içinde): akıllı başlangıç reçetesi + deprem.
 * `seismic` undefined = deprem açık ama SS/S1 boş → duvarın SS/S1'i korunur, yalnız hesap açılır.
 */
export function smartToWall(w: WallInput, s: SmartStart, seismic: SeismicInit | undefined): void {
  s.recipe(w);
  if (!seismic) { w.seismic.enabled = true; return; }
  if (!seismic.enabled) { w.seismic.enabled = false; return; }
  Object.assign(w.seismic, { enabled: true, inputMode: "map", SS: seismic.SS, S1: seismic.S1, siteClass: seismic.siteClass });
}

/**
 * Sihirbazı seçili duvara uygular: duvar + künye yaması (`info`, bkz. wizardInfoPatch) tek geri alma adımı
 * (updateWallAndInfo), ① tamam işareti; duvar
 * taranabilir tipteyse (parametrik taş / payandasız konsol) Akıllı tasarım girdisi H, L yazılır ve taze tarama istenir
 * (bitince en ekonomik aday SmartDesignLayout'ta kendiliğinden uygulanır). Payandalı / çokgen kesitte yalnız zemin ve yükler yazılır.
 */
export function applySmartToWall(wallId: string, s: SmartStart, seismic: SeismicInit | undefined, info: Partial<ProjectInfo> = {}): { applied: boolean; scan: boolean } {
  const st = useProjectStore.getState();
  const w = st.project?.walls.find((x) => x.id === wallId);
  if (!w || st.readOnly) return { applied: false, scan: false };
  st.updateWallAndInfo(wallId, (d) => smartToWall(d, s, seismic), info);
  useWizardStore.getState().markSoil(wallId);
  if (!scannable(w)) return { applied: true, scan: false };
  const next = useProjectStore.getState().project?.walls.find((x) => x.id === wallId) ?? w;
  const a = useAutoSizeStore.getState();
  a.setInputs(wallId, { height: s.height, length: next.length ?? 10 });
  a.requestRun(wallId, { fresh: true });
  return { applied: true, scan: true };
}
